<template>
    <div>
        <div class="provider-table">
            <div class="provider-table--header">
                <div class="provider-table--header__item">{{ $t('pages.network.ehr.table.provider') }}</div>
                <div v-if="practiceType === PracticeTypeEnum.PHYSICIAN" class="provider-table--header__item">
                    {{ $t('pages.network.ehr.table.specialty') }}
                </div>
                <div class="provider-table--header__item">{{ $t('pages.network.ehr.table.location') }}</div>
                <div class="provider-table--header__item">{{ $t('pages.network.ehr.table.insurance') }}</div>
                <div class="provider-table--header__item"></div>
            </div>
            <div v-if="practiceType === PracticeTypeEnum.PHYSICIAN" class="provider-table--wrapper">
                <div v-for="(element, index) in physicianConnections" :key="index" class="provider-table--content">
                    <div class="provider-table--content__item provider-table--content__item--provider">
                        <common-user-avatar :user="element.user" size="user-menu"></common-user-avatar>
                        <p class="provider-table--content__item--provider--name">{{ element.user.name }}</p>
                    </div>
                    <div class="provider-table--content__item provider-table--content__item--specialities">
                        <common-specialities :specialities="element.user.specialities"></common-specialities>
                    </div>
                    <div class="provider-table--content__item">
                        <p>{{ element?.user.practice.locations.find((item) => item.id === 0)?.address }}</p>
                        <p>{{ element?.user.practice.locations.find((item) => item.id === 0)?.city }}</p>
                    </div>
                    <div class="provider-table--content__item provider-table--content__item--insurance">
                        <common-badges-list
                            v-if="element.user.practice.insurancePlans?.length"
                            :openOnHover="true"
                            :listElements="element.user.practice.insurancePlans?.map((item) => item.name)"
                        ></common-badges-list>
                    </div>
                    <div class="provider-table--content__item provider-table--content__item--action">
                        <common-button
                            :label="$t('pages.network.ehr.table.availability')"
                            :isDisabled="!element?.target.isRegistrationComplete"
                            :isSubmittable="false"
                            type="primary"
                            @click="checkAvailabiliy(element)"
                        ></common-button>
                    </div>
                </div>
            </div>
            <div v-else>
                <div
                    v-for="(element, index) in ancillaryConnections"
                    :key="index"
                    class="provider-table--content provider-table--content__ancillary"
                >
                    <div class="provider-table--content__item provider-table--content__item--provider">
                        <common-ancillary-avatar
                            :ancillary="element.ancillary"
                            type="primary"
                            size="user-menu"
                        ></common-ancillary-avatar>
                        <p class="provider-table--content__item--provider--name">{{ element.ancillary?.name }}</p>
                    </div>

                    <div class="provider-table--content__item">
                        <p>{{ element?.ancillary.address }}</p>
                        <p>{{ element?.ancillary.city }}</p>
                    </div>
                    <div class="provider-table--content__item provider-table--content__item--insurance">
                        <common-badges-list
                            v-if="element?.ancillary.insurancePlans?.length"
                            :openOnHover="true"
                            :listElements="element.ancillary.insurancePlans?.map((item) => item.name)"
                        ></common-badges-list>
                    </div>
                    <div class="provider-table--content__item provider-table--content__item--action">
                        <common-button
                            :label="$t('pages.network.ehr.table.availability')"
                            :isDisabled="!element?.target.isRegistrationComplete"
                            :isSubmittable="false"
                            type="primary"
                            @click="checkAvailabiliy(element)"
                        ></common-button>
                    </div>
                </div>
            </div>

            <direct-referral
                v-if="isDirectReferralModalOpen"
                ref="directReferral"
                :ehr="true"
                :user="userData"
                :patientInsuranceId="patientInsuranceId"
                :activePracticeType="practiceType"
                @location-selected="toggleDirectReferralModal"
                @close="isDirectReferralModalOpen = false"
            ></direct-referral>
        </div>
        <common-button
            v-show="showLoadMoreButton"
            class="provider-table__footer"
            :isProcessing="isSending"
            :label="$t('pages.network.ehr.table.view_more')"
            type="primary"
            size="large"
            @click="loadMore"
        ></common-button>
    </div>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import DirectReferral from 'components/page-elements/referrals/DirectReferral'

    export default {
        mixins: [],

        components: { DirectReferral },

        props: {
            practiceType: { type: String, required: true },
            filters: { type: Object, required: false, default: null },
        },

        data() {
            return {
                PracticeTypeEnum: PracticeTypeEnum,
                pageSize: 0,
                physicianConnections: [],
                ancillaryConnections: [],
                physicianFilter: {
                    connectionDirection: 'All',
                    limit: this.pageSize,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                },
                ancillaryFilter: {
                    connectionDirection: 'All',
                    limit: this.pageSize,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                },
                insuranceId: undefined,
                specialityId: undefined,
                zipCode: undefined,
                totalCount: 0,
                isDirectReferralModalOpen: false,
                userData: null,
                showLoadMoreButton: false,
                isSending: false,
            }
        },

        methods: {
            fetchData(append) {
                this.isSending = true
                const filterString = this.practiceType === PracticeTypeEnum.PHYSICIAN ? 'physicianFilter' : 'ancillaryFilter'
                const request = {
                    connectionTargetPracticeType: this.practiceType === PracticeTypeEnum.PHYSICIAN ? 2 : 3,
                    connectionDirection: this[filterString].connectionDirection,
                    limit: this[filterString].limit,
                    offset: this[filterString].offset,
                    orderBy: this[filterString].orderBy,
                    totalCount: null,
                    filteredCount: true,
                    fields: null,
                    physicianId: this.$auth.getActiveMember().id,
                    withPractices: true,
                    registrationCompletionStatus: 'all',
                    searchTerm: null,
                    insuranceId: this.filters?.insuranceId || undefined,
                    specialityId: this.filters?.specialityId || undefined,
                    zipCode: this.filters?.zipCode || undefined,
                }
                return UsersAPI.getMyConnections(request).then((conns) => {
                    this.totalCount = parseInt(conns._headers['x-total-count'])
                    this.isSending = false
                    this.setFetchedData(conns, append)
                })
            },

            setFetchedData(conns, append) {
                if (append) {
                    let collection =
                        this.practiceType === PracticeTypeEnum.PHYSICIAN ? this.physicianConnections : this.ancillaryConnections
                    for (let i = 0; i < conns.length; ++i) {
                        collection.push(conns[i])
                    }
                } else {
                    if (this.practiceType === PracticeTypeEnum.PHYSICIAN) this.physicianConnections = conns
                    else this.ancillaryConnections = conns
                }
            },

            checkAvailabiliy(element) {
                this.toggleDirectReferralModal(element)
            },

            toggleDirectReferralModal(element) {
                this.userData = this.practiceType === PracticeTypeEnum.PHYSICIAN ? element.user : element.target
                this.isDirectReferralModalOpen = !this.isDirectReferralModalOpen
            },

            getMainLocationName(locations) {
                const mainLocation = locations.find((item) => item.id === 0)
                return mainLocation.name
            },

            loadMore() {
                if (this.practiceType === PracticeTypeEnum.PHYSICIAN) {
                    this.physicianFilter.offset = this.physicianFilter.offset + this.physicianFilter.limit
                    this.fetchData(true)
                } else {
                    this.ancillaryFilter.offset = this.ancillaryFilter.offset + this.ancillaryFilter.limit
                    this.fetchData(true)
                }
            },
        },

        watch: {
            practiceType() {
                this.fetchData()
            },

            physicianConnections() {
                this.showLoadMoreButton = this.totalCount > this.physicianConnections.length
            },

            ancillaryConnections() {
                this.showLoadMoreButton = this.totalCount > this.ancillaryConnections.length
            },

            filters() {
                if (this.filters?.pageSize) {
                    this.pageSize = this.filters.pageSize
                }
                this.physicianConnections = []
                this.ancillaryConnections = []
                this.physicianFilter.limit = this.pageSize
                this.ancillaryFilter.limit = this.pageSize
                this.physicianFilter.offset = 0
                this.ancillaryFilter.offset = 0
                this.fetchData()
            },
        },

        beforeCreate() {
            this.pageSize = 5
        },

        mounted() {
            this.patientInsuranceId = 246
            this.fetchData()
        },
    }
</script>

<style lang="scss">
    @import 'styles/components/page-elements/ehr/_provider-table';
</style>
